import { Box, Card, Stack, Typography, useTheme, useMediaQuery } from "@mui/material";
import { Chart } from "@/components/chart";
import { delftBlue, flame } from "@/theme/colors";

const OverviewChart = ({ data = null, isLoading = false }) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isSm = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isMd = useMediaQuery(theme.breakpoints.between("md", "lg"));

  const chartHeight = isXs ? 280 : isSm ? 300 : isMd ? 320 : 272;

  // Use API data if available, otherwise use empty data
  // Create a deep copy to ensure the object is extensible
  const chartData = data ? JSON.parse(JSON.stringify(data)) : {
    labels: [],
    series: [
      {
        name: "Company",
        data: [],
      },
      {
        name: "Activity",
        data: [],
      },
      {
        name: "Form",
        data: [],
      },
    ],
  };

  // Calculate max value for y-axis
  const allValues = [
    ...(chartData.series[0]?.data || []),
    ...(chartData.series[1]?.data || []),
    ...(chartData.series[2]?.data || []),
  ];
  const maxValue = allValues.length > 0 ? Math.max(...allValues) : 100;
  const yAxisMax = Math.ceil(maxValue * 1.2); // Add 20% padding

  // Create a deep copy of options to ensure it's extensible
  const chartOptions = JSON.parse(JSON.stringify({
    chart: {
      type: "line",
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
      offsetX: 0,
      offsetY: 0,
    },
    stroke: {
      width: 2,
      curve: "smooth",
    },
    markers: {
      size: 4,
      hover: {
        size: 6,
      },
    },
    xaxis: {
      categories: chartData.labels || [],
      labels: {
        style: {
          colors: theme.palette.neutral[600],
          fontSize: "13px",
          fontFamily: "Montserrat, sans-serif",
          fontWeight: 400,
        },
      },
      axisBorder: {
        show: true,
        color: theme.palette.neutral[600],
      },
      axisTicks: {
        show: true,
        color: theme.palette.neutral[600],
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: theme.palette.neutral[600],
          fontSize: "13px",
          fontFamily: "Montserrat, sans-serif",
          fontWeight: 400,
        },
      },
      min: 0,
      max: yAxisMax || 100,
      tickAmount: 5,
    },
    grid: {
      borderColor: theme.palette.neutral[100],
      strokeDashArray: 3,
      xaxis: {
        lines: {
          show: true,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    legend: {
      show: true,
      position: "bottom",
      horizontalAlign: "center",
      markers: {
        width: 8,
        height: 8,
        radius: 4,
      },
      labels: {
        colors: delftBlue[500],
        fontSize: "11px",
        fontFamily: "Montserrat, sans-serif",
        fontWeight: 400,
      },
    },
    tooltip: {
      theme: "light",
    },
    colors: chartData.series.map((_, index) => {
      const colorMap = [flame[500], delftBlue[500], delftBlue[400]];
      return colorMap[index] || delftBlue[500];
    }),
  }));

  return (
    <Card
      sx={{
        width: { xs: "100%", sm: "100%", md: "100%", lg: 752 },
        minHeight: { xs: 300, sm: 320, md: 340 },
        position: "relative",
        background: "white",
        borderRadius: 2,
        outline: `1px ${delftBlue[100]} solid`,
        outlineOffset: "-1px",
        padding: { xs: 1.5, sm: 2 },
        paddingBottom: { xs: 6, sm: 7 },
        overflow: "visible",
      }}
    >
      <Box
        sx={{
          height: { xs: 280, sm: 300, md: 320, lg: 272 },
          width: "100%",
          overflow: "visible",
          position: "relative",
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography
              sx={{
                color: delftBlue[500],
                fontSize: { xs: 12, sm: 13 },
                fontFamily: "Montserrat, sans-serif",
              }}
            >
              Loading chart data...
            </Typography>
          </Box>
        ) : (
          <Chart
            type="line"
            series={JSON.parse(JSON.stringify(chartData.series))}
            options={chartOptions}
            height={chartHeight}
          />
        )}
      </Box>

      <Box
        sx={{
          position: "absolute",
          left: "50%",
          transform: "translateX(-50%)",
          bottom: { xs: 12, sm: 16 },
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: { xs: 1.5, sm: 2, md: 3 },
          flexWrap: "wrap",
          width: { xs: "90%", sm: "auto" },
        }}
      >
        {chartData.series.map((seriesItem, index) => {
          const colors = [flame[500], delftBlue[500], delftBlue[400]];
          const color = colors[index] || delftBlue[500];
          
          return (
            <Stack key={seriesItem.name} direction="row" spacing={0.5} alignItems="center">
              <Box
                sx={{
                  width: { xs: 7, sm: 8 },
                  height: { xs: 7, sm: 8 },
                  borderRadius: "50%",
                  background: color,
                }}
              />
              <Typography
                sx={{
                  color: delftBlue[500],
                  fontSize: { xs: 10, sm: 11 },
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 400,
                  lineHeight: 1.45,
                }}
              >
                {seriesItem.name}
              </Typography>
            </Stack>
          );
        })}
      </Box>
    </Card>
  );
};

export default OverviewChart;

